import { useEffect, FC, useState, ReactNode } from 'react';
import classNames from 'classnames';

import { Checkbox } from '@hh.ru/magritte-ui';

import styles from './StoreDevHelperProvider.module.less';

type StoreDevHelperProviderProps = { serviceName: string; place?: string; children: ReactNode };
type ActionCallback = (result?: { [key: string]: unknown }) => void;

let _StoreDevHelperProvider: FC<StoreDevHelperProviderProps> = ({ children }) => <>{children}</>;

const showDevInfoToConsole = () => {
    // eslint-disable-next-line no-console
    console.log(
        "%cВнимание разработчик! Тебе доступна 'store' утилита (позволяет получать текущее состояние и подписываться" +
            ' на изменения' +
            ' в redux store). Дока: https://github.com/hhru/front-packages/tree/master/redux-dev-helper.' +
            ' Пример: window.store.myService.myPlace(); Для удобства используй UI, который вызывается с помощью комбинации клавиш: Ctrl + Shift + 1.',
        'font-size: 14px;'
    );
};

if (process.env.HH_BUILD_MODE === 'development') {
    const StoreDevHelperRenderer: FC<
        Omit<StoreDevHelperProviderProps, 'children'> & { onVisible: (visible: boolean) => void }
    > = ({ serviceName, place = 'page', onVisible }) => {
        const [isControlsVisible, setIsControlsVisible] = useState<boolean>(false);
        const [isIncludeUnchangedFields, setIsIncludeUnchangedFields] = useState<boolean>(true);
        const [fieldValue, setFieldValue] = useState<string>('');
        const [isFieldNotFound, setIsFieldNotFound] = useState<boolean>(false);
        const handleStore = window.store[serviceName][place];

        const handleStoreAction = (actionCallback: ActionCallback) => {
            const result = handleStore(fieldValue || '', isIncludeUnchangedFields);

            if (fieldValue && Object.keys(result).length === 0) {
                console.warn(`Поле ${fieldValue} отсутствует в ${serviceName}: ${place}`);
                setIsFieldNotFound(true);
                return;
            }

            actionCallback(result);
            setIsFieldNotFound(false);
        };

        const drawStoreToConsole = () => {
            handleStoreAction((result) => {
                // eslint-disable-next-line no-console
                console.log(`Service: ${serviceName}, Place: ${place}`, result);
            });
        };

        const subscribeToStore = () => {
            handleStoreAction(() => {
                handleStore.subscribe(fieldValue || '', isIncludeUnchangedFields);
            });
        };

        const unSubscribeFromStore = () => {
            handleStore.unsubscribe();
        };

        const handleShowStores = () => {
            setIsControlsVisible((value) => !value);
        };

        useEffect(() => {
            onVisible(isControlsVisible);
        }, [onVisible, isControlsVisible]);

        useEffect(() => {
            showDevInfoToConsole();

            const handleKeydown = (event: KeyboardEvent) => {
                if (event.ctrlKey && event.shiftKey && event.key === '1') {
                    event.preventDefault();
                    handleShowStores();
                }
            };
            document.addEventListener('keydown', handleKeydown);

            return () => {
                document.removeEventListener('keydown', handleKeydown);
            };
        }, []);

        if (!isControlsVisible) {
            return null;
        }

        return (
            <div className={styles.storeControl}>
                <div className={styles.storeControlInner}>
                    <span>
                        Service Name: <b>{serviceName}</b>. Place: <b>{place}</b>
                    </span>

                    <input
                        className={classNames(styles.fieldInput, isFieldNotFound && styles.fieldError)}
                        type="text"
                        placeholder="Store Field: All"
                        value={fieldValue}
                        list={`field-list-${serviceName}-${place}`}
                        onChange={(event) => {
                            setFieldValue(event.target.value);
                        }}
                    />

                    <datalist id={`field-list-${serviceName}-${place}`}>
                        {Object.keys(handleStore()).map((fieldName) => {
                            return <option key={fieldName} value={fieldName}></option>;
                        })}
                    </datalist>

                    <button
                        className={styles.drawStoreBtn}
                        type="button"
                        onClick={() => {
                            drawStoreToConsole();
                        }}
                    >
                        Log store
                    </button>

                    <button
                        className={styles.drawStoreBtn}
                        type="button"
                        onClick={() => {
                            subscribeToStore();
                        }}
                    >
                        Subscribe
                    </button>

                    <button
                        className={styles.drawStoreBtn}
                        type="button"
                        onClick={() => {
                            unSubscribeFromStore();
                        }}
                    >
                        Unsubscribe
                    </button>

                    <label className={styles.label}>
                        <Checkbox
                            checked={isIncludeUnchangedFields}
                            onChange={() => {
                                setIsIncludeUnchangedFields((value) => !value);
                            }}
                        />
                        Include unchanged fields
                    </label>
                </div>
            </div>
        );
    };

    const __StoreDevHelperProvider: FC<StoreDevHelperProviderProps> = ({ serviceName, place, children }) => {
        const [isVisibleOnClient, setIsVisibleOnClient] = useState<boolean>(false);
        const [isControlsVisible, setIsControlsVisible] = useState<boolean>(false);

        useEffect(() => {
            setIsVisibleOnClient(true);
        }, []);

        return (
            <div className={isControlsVisible ? styles.storeControlContainer : ''}>
                {isVisibleOnClient && (
                    <StoreDevHelperRenderer serviceName={serviceName} place={place} onVisible={setIsControlsVisible} />
                )}
                {children}
            </div>
        );
    };

    _StoreDevHelperProvider = __StoreDevHelperProvider;
}

const StoreDevHelperProvider = _StoreDevHelperProvider;

export default StoreDevHelperProvider;
